// src/pages/Media.tsx — Marketing › Media (docs/media-library.md): every building and flat image, floorplan, EPC and
// video in folders. The tree on the left (buildings → Building photos and flats → Photos / Floorplans / EPC, plus
// staff folders), the open folder's breadcrumbs and toolbar, and its grid (components/media/MediaGrid). The open folder
// is in the URL (?folder=) so a link lands on it. Staff folders can be made, renamed and deleted; the automatic ones
// follow their building or flat.
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Search } from 'lucide-react';
import { ActionMenu, Button, Input, PageHeader, Sheet, Skeleton } from '../components/ui';
import { Confirm, FormError } from '../components/forms/common';
import { FolderTree } from '../components/media/FolderTree';
import { MediaGrid } from '../components/media/MediaGrid';
import { useToast } from '../components/media/toast';
import { useCan } from '../hooks/usePermission';
import { indexTree, mediaApi, type MediaFolder } from '../lib/media';

export default function MediaPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder');
  const open = useCallback((id: string | null) => {
    const n = new URLSearchParams(params);
    if (id) n.set('folder', id); else n.delete('folder');
    setParams(n);
  }, [params, setParams]);
  const can = { create: useCan('media.create'), update: useCan('media.update'), del: useCan('media.delete') };

  const tree = useQuery({ queryKey: ['media', 'tree'], queryFn: () => mediaApi.tree() });
  const detail = useQuery({ queryKey: ['media', 'folder', folderId], queryFn: () => mediaApi.folder(folderId!), enabled: !!folderId });
  const byId = useMemo(() => indexTree(tree.data ?? []), [tree.data]);
  const f = folderId ? detail.data : null;
  // Sub-folders with their counts (from the tree, which carries them).
  const subfolders: MediaFolder[] = folderId ? (byId.get(folderId)?.children ?? detail.data?.children ?? []) : tree.data ?? [];
  const [filter, setFilter] = useState('');

  // ── Staff folders ──
  const [naming, setNaming] = useState<null | { mode: 'new' | 'rename'; name: string }>(null);
  const [nameError, setNameError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const refreshFolders = () => { void qc.invalidateQueries({ queryKey: ['media', 'tree'] }); void qc.invalidateQueries({ queryKey: ['media', 'folder'] }); };
  const saveName = async () => {
    if (!naming) return;
    setSaving(true); setNameError(null);
    try {
      if (naming.mode === 'new') {
        const made = await mediaApi.createFolder({ parentId: folderId, name: naming.name.trim() });
        toast.ok(`Folder “${made.name}” made`);
        open(made.id);
      } else if (folderId) {
        await mediaApi.renameFolder(folderId, naming.name.trim());
        toast.ok('Folder renamed');
      }
      setNaming(null); refreshFolders();
    } catch (e) { setNameError(e); }
    setSaving(false);
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const removeFolder = async () => {
    if (!f) return;
    setDeleting(true);
    try {
      await mediaApi.deleteFolder(f.id);
      setConfirmDelete(false);
      toast.ok(`Folder “${f.name}” deleted`);
      open(f.parentId);
      refreshFolders();
    } catch (e) { toast.fail(e); }
    setDeleting(false);
  };

  const crumbs = f?.path ?? [];
  const header = (
    <nav aria-label="Folder path" className="mb-3 flex min-h-8 flex-wrap items-center gap-1 text-[13px]">
      <button type="button" onClick={() => open(null)} className={`rounded px-1 font-medium transition ${folderId ? 'text-ink-500 hover:text-ink-900' : 'text-ink-900'}`}>Media</button>
      {folderId && detail.isLoading && <><ChevronRight className="h-3.5 w-3.5 text-ink-300" /><Skeleton className="h-3.5 w-40" /></>}
      {crumbs.map((c, i) => (
        <span key={c.id} className="inline-flex items-center gap-1">
          <ChevronRight className="h-3.5 w-3.5 text-ink-300" />
          {i === crumbs.length - 1
            ? <span className="px-1 font-semibold text-ink-900">{c.name}</span>
            : <button type="button" onClick={() => open(c.id)} className="rounded px-1 font-medium text-ink-500 transition hover:text-ink-900">{c.name}</button>}
        </span>
      ))}
      {f?.archived && <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">Archived</span>}
      {f?.isAuto && <span className="ml-2 text-[11.5px] text-ink-400">Made automatically — follows its {f.kind.startsWith('UNIT') ? 'flat' : 'building'}</span>}
    </nav>
  );

  const folderMenu = f && f.kind === 'CUSTOM' && (can.update || can.del) ? (
    <ActionMenu label="Folder actions" items={[
      { label: 'Rename folder', icon: 'PencilLine', can: 'media.update', onClick: () => { setNameError(null); setNaming({ mode: 'rename', name: f.name }); } },
      { label: 'Delete folder', icon: 'Trash2', can: 'media.delete', danger: true, onClick: () => setConfirmDelete(true) },
    ]} />
  ) : null;

  const aside = (
    <aside className="hidden w-[272px] shrink-0 lg:block">
      <div className="surface sticky top-3 flex max-h-[calc(100dvh-7rem)] flex-col overflow-hidden">
        <div className="relative shrink-0 border-b border-ink-100 p-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a building or folder" aria-label="Find a building or folder" className="w-full pl-7" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {tree.isLoading ? <div className="space-y-2 p-2">{Array.from({ length: 10 }, (_, i) => <Skeleton key={i} className="h-5 w-full" />)}</div>
            : tree.isError ? <p className="p-3 text-[12px] text-red-600">The folders could not be loaded.</p>
            : <FolderTree roots={tree.data ?? []} selectedId={folderId} onSelect={open} dropTargets={can.update} filter={filter} />}
        </div>
      </div>
    </aside>
  );

  return (
    <div>
      <PageHeader eyebrow="Marketing" title="Media" />
      {folderId && detail.isError ? (
        <div className="flex gap-4">{aside}<div className="surface flex-1 p-8 text-center text-[13px] text-ink-500">This folder is not there any more, or not in your area. <button type="button" className="font-medium text-bronze-600 hover:underline" onClick={() => open(null)}>Back to Media</button></div></div>
      ) : (
        <MediaGrid
          folder={f ? { id: f.id, name: f.name, kind: f.kind, isAuto: f.isAuto, archived: f.archived } : null}
          subfolders={subfolders}
          onOpenFolder={open}
          aside={aside}
          header={header}
          toolbarEnd={<>
            {can.create && !f?.archived && (!folderId || !!f) && <Button size="sm" variant="secondary" icon="FolderPlus" onClick={() => { setNameError(null); setNaming({ mode: 'new', name: '' }); }}>New folder</Button>}
            {folderMenu}
          </>}
          onChanged={refreshFolders}
        />
      )}

      <Sheet open={!!naming} onClose={() => setNaming(null)} size="sm"
        title={naming?.mode === 'rename' ? 'Rename folder' : 'New folder'}
        subtitle={naming?.mode === 'new' ? (f ? `Inside ${f.name}` : 'At the top of Media') : undefined}
        footer={<><Button variant="secondary" onClick={() => setNaming(null)}>Cancel</Button><Button disabled={!naming?.name.trim() || saving} onClick={() => void saveName()}>{saving ? 'Saving…' : naming?.mode === 'rename' ? 'Rename' : 'Make folder'}</Button></>}>
        <div className="space-y-3">
          <Input autoFocus value={naming?.name ?? ''} maxLength={160} placeholder="Marketing, Amenities, Launch 2027…" className="w-full"
            onChange={(e) => setNaming((n) => (n ? { ...n, name: e.target.value } : n))}
            onKeyDown={(e) => { if (e.key === 'Enter' && naming?.name.trim()) void saveName(); }} />
          <FormError error={nameError} />
        </div>
      </Sheet>
      <Confirm open={confirmDelete} onClose={() => setConfirmDelete(false)} pending={deleting} confirmLabel="Delete folder" title={`Delete “${f?.name ?? ''}”?`}
        onConfirm={() => void removeFolder()}
        body={<>The folder, its sub-folders and every file in them are deleted. Copies of those files in other folders are kept.</>} />
      {toast.node}
    </div>
  );
}
